
import React from 'react';
import { ArrowRight, FileText, Shield, Star, AlertTriangle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const Terms: React.FC = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className={cn("mb-6 md:mb-10", isMobile ? "text-center" : "text-center")}>
          <div className={cn(
            "inline-flex items-center justify-center rounded-full mb-4",
            isMobile ? "w-12 h-12" : "w-16 h-16 mb-5"
          )}>
            <FileText className={cn(
              "text-blue-600 dark:text-blue-400",
              isMobile ? "w-6 h-6" : "w-8 h-8"
            )} />
          </div>
          <h1 className={cn(
            "font-bold mb-3 text-gradient",
            isMobile ? "text-3xl" : "text-4xl md:text-5xl mb-4"
          )}>
            Terms of Service
          </h1>
          <p className={cn(
            "text-muted-foreground max-w-2xl mx-auto",
            isMobile ? "text-base" : "text-lg"
          )}>
            Please read these terms carefully before using our platform.
          </p>
        </div>
        
        {/* Main content */}
        <div className={cn(
          "rounded-xl mb-8",
          isMobile ? "p-5" : "rounded-2xl p-8 lg:p-10 mb-10",
          theme === 'light' 
            ? "bg-white/80 backdrop-blur-sm shadow-sm border border-slate-200/50" 
            : "bg-slate/5 backdrop-blur-sm border border-slate/10 shadow-md"
        )}>
          <div className={cn(
            "prose dark:prose-invert max-w-none",
            isMobile ? "prose-sm" : "prose-lg"
          )}>
            <section className={cn("mb-5", isMobile ? "" : "mb-6")}>
              <p className={cn(
                "italic mb-4",
                isMobile ? "text-xs text-left" : "text-sm text-center mb-6"
              )}>
                Last Updated: March 25, 2025
              </p>
              <p className={cn("mb-4", isMobile ? "text-left" : "")}>
                Welcome to <strong>Chatrix</strong>! These Terms of Service ("Terms") and Privacy Policy govern your use of our website and services. 
                By accessing or using <strong>Chatrix</strong>, you agree to comply with these Terms. If you do not agree, please do not use the website.
              </p>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <div className={cn("flex items-start gap-3", isMobile ? "mb-3" : "gap-4 mb-4")}>
                <div className={cn(
                  "rounded-lg flex-shrink-0",
                  isMobile ? "p-1.5 mt-0.5" : "p-2",
                  theme === 'light' ? "bg-blue-50" : "bg-blue-900/20"
                )}>
                  <Shield className={cn(
                    isMobile ? "w-4 h-4" : "w-5 h-5", 
                    theme === 'light' ? "text-blue-600" : "text-blue-400"
                  )} />
                </div>
                <div>
                  <h2 className={cn(
                    "font-semibold",
                    isMobile ? "text-xl mb-3" : "text-2xl mb-4"
                  )}>
                    1. General Information
                  </h2>
                  <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>Chatrix</strong> is a <strong>hobby project</strong> developed by <strong>Alok Mishra</strong>.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left">We reserve the right to modify, suspend, or discontinue services at any time without prior notice.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <div className={cn("flex items-start gap-3", isMobile ? "mb-3" : "gap-4 mb-4")}>
                <div className={cn(
                  "rounded-lg flex-shrink-0",
                  isMobile ? "p-1.5 mt-0.5" : "p-2",
                  theme === 'light' ? "bg-blue-50" : "bg-blue-900/20"
                )}>
                  <Star className={cn(
                    isMobile ? "w-4 h-4" : "w-5 h-5", 
                    theme === 'light' ? "text-blue-600" : "text-blue-400"
                  )} />
                </div>
                <div>
                  <h2 className={cn(
                    "font-semibold",
                    isMobile ? "text-xl mb-3" : "text-2xl mb-4"
                  )}>
                    2. Acceptable Use Policy
                  </h2>
                  <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                    To ensure a safe and enjoyable experience for all users, you must adhere to the following rules:
                  </p>
                  
                  <div className={cn(
                    "rounded-xl mb-4",
                    isMobile ? "p-4 mb-3" : "p-6 mb-6",
                    theme === 'light' ? "bg-slate-50" : "bg-slate/10"
                  )}>
                    <ul className={cn(
                      "space-y-2",
                      isMobile ? "space-y-1.5" : "space-y-3"
                    )}>
                      <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                        <ArrowRight className={cn(
                          "flex-shrink-0 text-blue-500",
                          isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                        )} />
                        <span className="text-left"><strong>Age Requirement</strong>: You must be at least <strong>13 years old</strong> to use <strong>Chatrix</strong>.</span>
                      </li>
                      <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                        <ArrowRight className={cn(
                          "flex-shrink-0 text-blue-500",
                          isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                        )} />
                        <div className="text-left">
                          <strong>Prohibited Content</strong>:
                          <ul className={cn(
                            "ml-4 mt-1.5",
                            isMobile ? "space-y-1.5" : "space-y-2 ml-6 mt-2"
                          )}>
                            <li className={cn("flex items-start gap-1", isMobile ? "" : "gap-2")}>
                              <ArrowRight className={cn(
                                "flex-shrink-0 text-blue-500",
                                isMobile ? "w-2.5 h-2.5 mt-1" : "w-3 h-3 mt-1"
                              )} />
                              <span className="text-left"><strong>NSFW (Not Safe For Work) or adult content is strictly prohibited</strong>.</span>
                            </li>
                            <li className={cn("flex items-start gap-1", isMobile ? "" : "gap-2")}>
                              <ArrowRight className={cn(
                                "flex-shrink-0 text-blue-500",
                                isMobile ? "w-2.5 h-2.5 mt-1" : "w-3 h-3 mt-1"
                              )} />
                              <span className="text-left"><strong>Hate speech, threats, harassment, or any form of abusive behavior will not be tolerated</strong>.</span>
                            </li>
                            <li className={cn("flex items-start gap-1", isMobile ? "" : "gap-2")}>
                              <ArrowRight className={cn(
                                "flex-shrink-0 text-blue-500",
                                isMobile ? "w-2.5 h-2.5 mt-1" : "w-3 h-3 mt-1"
                              )} />
                              <span className="text-left"><strong>Illegal activities, including but not limited to fraud, hacking, or spamming, are strictly forbidden</strong>.</span>
                            </li>
                          </ul>
                        </div>
                      </li>
                      <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                        <ArrowRight className={cn(
                          "flex-shrink-0 text-blue-500",
                          isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                        )} />
                        <span className="text-left"><strong>Accountability</strong>: You are <strong>solely responsible</strong> for your interactions and content shared on <strong>Chatrix</strong>.</span>
                      </li>
                      <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                        <ArrowRight className={cn(
                          "flex-shrink-0 text-blue-500",
                          isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                        )} />
                        <span className="text-left"><strong>Enforcement</strong>: Users found violating these rules <strong>may be permanently banned</strong> without notice.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <div className={cn("flex items-start gap-3", isMobile ? "mb-3" : "gap-4 mb-4")}>
                <div className={cn(
                  "rounded-lg flex-shrink-0",
                  isMobile ? "p-1.5 mt-0.5" : "p-2",
                  theme === 'light' ? "bg-red-50" : "bg-red-900/20"
                )}>
                  <AlertTriangle className={cn(
                    isMobile ? "w-4 h-4" : "w-5 h-5", 
                    theme === 'light' ? "text-red-600" : "text-red-400"
                  )} />
                </div>
                <div>
                  <h2 className={cn(
                    "font-semibold",
                    isMobile ? "text-xl mb-3" : "text-2xl mb-4"
                  )}>
                    3. Disclaimer of Liability
                  </h2>
                  <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>Use at Your Own Risk</strong>: <strong>Chatrix</strong> is an <strong>entertainment and communication platform</strong>. We <strong>do not monitor or control user-generated content</strong>.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>No Responsibility for User Actions</strong>: <strong>We are not responsible for any actions, messages, or consequences arising from your use of the platform</strong>.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>Security Risks</strong>: While we take reasonable precautions, <strong>we cannot guarantee absolute security</strong> from cyber threats.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                4. Intellectual Property Rights
              </h2>
              <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left"><strong>Ownership</strong>: You <strong>retain ownership</strong> of any content you share, but by using <strong>Chatrix</strong>, you grant us a <strong>non-exclusive, worldwide license</strong> to display it within the platform.</span>
                </li>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left"><strong>Respect for Copyright</strong>: Do not share copyrighted materials unless you have the legal right to do so.</span>
                </li>
              </ul>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                5. Termination of Use
              </h2>
              <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left">We <strong>reserve the right</strong> to terminate, suspend, or restrict access to any user who violates our Terms or engages in harmful behavior.</span>
                </li>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left">If you are found engaging in <strong>illegal activities</strong>, your access <strong>will be revoked immediately</strong>.</span>
                </li>
              </ul>
            </section>
            
            <section>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                Final Note
              </h2>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                By using <strong>Chatrix</strong>, you acknowledge and agree to these Terms and our Privacy Policy. We <strong>may update these terms at any time</strong>, and it is your responsibility to review them periodically.
              </p>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                Enjoy using <strong>Chatrix</strong> responsibly and respectfully! 🚀
              </p>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                For any inquiries regarding these Terms, please contact us at <a href="mailto:contact@chatrix.fun" className="text-blue-600 dark:text-blue-400 underline underline-offset-2">contact@chatrix.fun</a>.
              </p>
            </section>
          </div>
        </div>
        
        {/* Updated date */}
        <div className="text-center text-sm text-muted-foreground">
          Last updated: March 25, 2025
        </div>
      </div>
    </div>
  );
};

export default Terms;
